import type { CreativesSurface } from '@alison/analytics';
import { AnalyticsService } from '@alison/analytics/client';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { FilterCheckbox } from '../filters/FilterCheckbox.js';
import { MAX_TOP_METRICS, MIN_TOP_METRICS, type PickableMetric } from './data/selection.js';

const FOOTER_BUTTON_SX = { height: 28, px: 3 };

interface CustomizeMetricsProps {
  metrics: PickableMetric[];
  selected: string[];
  onChange: (selected: string[]) => void;
  surface: CreativesSurface;
}

function openingOrder(metrics: PickableMetric[], selected: string[]): PickableMetric[] {
  const selectedNames = new Set(selected);
  return [...metrics].sort((a, b) => {
    const priority = Number(selectedNames.has(b.name)) - Number(selectedNames.has(a.name));
    return priority !== 0 ? priority : a.label.localeCompare(b.label);
  });
}

export function CustomizeMetrics({
  metrics,
  selected,
  onChange,
  surface,
}: CustomizeMetricsProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<string[]>(selected);
  const [ordered, setOrdered] = useState<PickableMetric[]>(metrics);

  const open = (event: MouseEvent<HTMLElement>): void => {
    setDraft(selected);
    setOrdered(openingOrder(metrics, selected));
    setAnchor(event.currentTarget);
  };

  const toggle = (name: string): void => {
    setDraft(draft.includes(name) ? draft.filter((current) => current !== name) : [...draft, name]);
  };

  const apply = (): void => {
    AnalyticsService.track('Top Metrics - Customize', { surface, metrics: draft });
    onChange(draft);
    setAnchor(null);
  };

  return (
    <>
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={open}
        disabled={metrics.length === 0}
        sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main', cursor: 'pointer' }}
      >
        Customize
      </Link>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              mt: 1.5,
              width: 280,
              p: 1.5,
              border: '1px solid',
              borderColor: theme.palette.border.default,
              boxShadow: theme.shadows[5],
            }),
          },
        }}
      >
        <Typography sx={{ fontSize: 11, color: 'text.secondary', pt: 1, px: 1.5, pb: 1.5 }}>
          {`Choose ${MIN_TOP_METRICS}–${MAX_TOP_METRICS} summary tiles · `}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {`${draft.length} selected`}
          </Box>
          . Values follow your selection when creatives are selected.
        </Typography>
        <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
          {ordered.map((metric) => {
            const checked = draft.includes(metric.name);
            const atLimit = checked
              ? draft.length <= MIN_TOP_METRICS
              : draft.length >= MAX_TOP_METRICS;
            return (
              <FormControlLabel
                key={metric.name}
                control={
                  <Checkbox
                    disableRipple
                    checked={checked}
                    disabled={atLimit}
                    onChange={() => toggle(metric.name)}
                    icon={<FilterCheckbox checked={false} />}
                    checkedIcon={<FilterCheckbox checked />}
                    sx={{ p: 0 }}
                  />
                }
                label={
                  <Box component="span" sx={{ flex: 1, fontSize: 12, fontWeight: 600 }}>
                    {metric.label}
                  </Box>
                }
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  m: 0,
                  py: 2,
                  px: 3,
                  borderRadius: `${theme.radius.md}px`,
                  transition: theme.transitions.create('background-color', {
                    duration: theme.transitions.duration.shortest,
                  }),
                  '&:hover': { bgcolor: 'background.page' },
                })}
              />
            );
          })}
        </Box>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
            mt: 1.5,
            py: 2,
            px: 2.5,
            borderTop: '1px solid',
            borderColor: theme.palette.border.default,
            bgcolor: 'background.page',
          })}
        >
          <Button
            variant="outlined"
            onClick={() => setAnchor(null)}
            sx={(theme) => ({
              ...FOOTER_BUTTON_SX,
              color: 'text.secondary',
              borderColor: theme.palette.border.default,
              bgcolor: 'background.paper',
              '&:hover': { color: 'text.primary', borderColor: theme.palette.text.secondary },
            })}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={apply} sx={FOOTER_BUTTON_SX}>
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}
